import { useState, useCallback, useEffect } from "react";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import PortalPopup from "../../components/PortalPopup";
import { useNavigate, useLocation } from "react-router-dom";
import NavigationPanel from "../../components/NavigationPanel";
import Breadcrumb from "../../components/Breadcrumb";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import Footer from "../../components/Footer";
import { FileText, Image, File, Eye, Download, X } from "lucide-react";
import { documentService } from "../../services/documentService";

const AIIndicators2 = () => {
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
  const [currentPreviewDocument, setCurrentPreviewDocument] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [riskDistribution, setRiskDistribution] = useState({
    riskFlags: 0,
    safeDocuments: 100,
  });
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [overallRisk, setOverallRisk] = useState(null);
  const [timePeriod, setTimePeriod] = useState("months");
  const [docFilter, setDocFilter] = useState("all");
  const [docSearch, setDocSearch] = useState("");

  const { name, amount, risk, aiScore, dueDate, id, type, originalData } =
    location.state || {};

  const clientData = {
    name: name || "Paras Mistry",
    amount: amount,
    risk: risk || "High",
    aiScore: aiScore || "28%",
    dueDate: dueDate || "15 Aug 2025",
    id: id || "N/A",
    type: type || "",
    originalData: originalData || {},
  };

  const getRiskColor = (r) => {
    switch (r?.toLowerCase()) {
      case "high":
        return "#DC2626";
      case "medium":
        return "#D97706";
      case "low":
        return "#059669";
      default:
        return "#64748B";
    }
  };

  const getRiskBg = (r) => {
    switch (r?.toLowerCase()) {
      case "high":
        return "#FEF2F2";
      case "medium":
        return "#FFFBEB";
      case "low":
        return "#F0FDF4";
      default:
        return "#F8FAFC";
    }
  };

  const getRiskAccent = (r) => {
    switch (r?.toLowerCase()) {
      case "high":
        return "#DC2626";
      case "medium":
        return "#D97706";
      case "low":
        return "#059669";
      default:
        return "#64748B";
    }
  };

  // ── Document helpers ──────────────────────────────────────────────────────

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";
    const n = parseInt(bytes);
    if (isNaN(n)) return "Unknown size";
    if (n < 1024) return n + " bytes";
    if (n < 1048576) return (n / 1024).toFixed(1) + " KB";
    return (n / 1048576).toFixed(1) + " MB";
  };

  const getFileKind = (f) => {
    if (!f) return "Unknown file";
    const ext = f.includes("/")
      ? f.split("/")[1]
      : f.split(".").pop().toLowerCase();
    const map = {
      pdf: "PDF document",
      msword: "Word document",
      "vnd.openxmlformats-officedocument.wordprocessingml.document":
        "Word document",
      doc: "Word document",
      docx: "Word document",
      png: "PNG image",
      jpeg: "JPEG image",
      jpg: "JPEG image",
      plain: "Text document",
      txt: "Text document",
      "vnd.ms-excel": "Excel spreadsheet",
      "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        "Excel spreadsheet",
      xls: "Excel spreadsheet",
      xlsx: "Excel spreadsheet",
    };
    return map[ext] || `${ext.toUpperCase()} file`;
  };

  const formatDate = (ds) => {
    if (!ds) return "Unknown date";
    try {
      const d = new Date(ds);
      return (
        d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " at " +
        d.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    } catch {
      return "Invalid date";
    }
  };

  const getFileIcon = (f) => {
    if (!f) return <File style={{ color: "#94A3B8" }} size={22} />;
    const ext = f.includes("/")
      ? f.split("/")[1]
      : f.split(".").pop().toLowerCase();
    if (["pdf"].includes(ext))
      return <FileText style={{ color: "#EF4444" }} size={22} />;
    if (
      [
        "doc",
        "docx",
        "msword",
        "vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(ext)
    )
      return <FileText style={{ color: "#2563EB" }} size={22} />;
    if (
      [
        "png",
        "jpg",
        "jpeg",
        "gif",
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
      ].includes(ext)
    )
      return <Image style={{ color: "#0891B2" }} size={22} />;
    if (
      [
        "xls",
        "xlsx",
        "vnd.ms-excel",
        "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ].includes(ext)
    )
      return <FileText style={{ color: "#059669" }} size={22} />;
    return <File style={{ color: "#94A3B8" }} size={22} />;
  };

  const checkDocumentForRiskFlags = (doc) => {
    if (!doc) return false;
    const riskKeywords = [
      "default",
      "overdue",
      "late",
      "warning",
      "notice",
      "demand",
      "collection",
      "legal",
      "court",
      "summons",
      "judgment",
      "lien",
      "foreclosure",
      "eviction",
      "breach",
      "violation",
      "termination",
      "suspension",
      "cancellation",
    ];
    const fileName = doc.name?.toLowerCase() || "";
    if (riskKeywords.some((k) => fileName.includes(k))) return true;
    if (doc.analysis) {
      const txt = JSON.stringify(doc.analysis).toLowerCase();
      if (
        [
          "high risk",
          "red flag",
          "suspicious",
          "fraud",
          "default",
          "non-payment",
          "delinquent",
          "overdue",
          "violation",
        ].some((k) => txt.includes(k))
      )
        return true;
      if (
        doc.analysis.sentiment === "negative" ||
        doc.analysis.riskLevel === "high"
      )
        return true;
    }
    return false;
  };

  const analyzeDocumentsForRisk = (docs) => {
    if (!docs?.length) return { riskFlags: 0, safeDocuments: 100 };
    const flagCount = docs.filter((d) => checkDocumentForRiskFlags(d)).length;
    const riskPct = Math.round((flagCount / docs.length) * 100);
    return { riskFlags: riskPct, safeDocuments: 100 - riskPct };
  };

  useEffect(() => {
    if (documents.length > 0)
      setRiskDistribution(analyzeDocumentsForRisk(documents));
  }, [documents]);

  const handleAnalyzeAllDocuments = async () => {
    try {
      setAnalyzing(true);
      const result = await documentService.analyzeAllDocuments(clientData.id);
      setAnalysis(result);
      setOverallRisk(result.overallRisk);
      await fetchDocumentsWithAnalysisPreserved();
    } catch (e) {
      console.error("Analysis failed:", e);
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchDocumentsWithAnalysisPreserved = async () => {
    try {
      setLoadingDocuments(true);
      const result = await documentService.getDocumentsWithAnalysis(
        clientData.id,
      );
      const formatted = result.documents.map((doc) => ({
        ...doc,
        icon: getFileIcon(doc.name || doc.mimeType),
        kind: getFileKind(doc.name || doc.mimeType),
        size: formatFileSize(doc.size),
        date: formatDate(doc.uploadedDate || doc.createdAt),
        hasRiskFlag: checkDocumentForRiskFlags(doc),
      }));
      setDocuments(formatted);
      setOverallRisk(result.overallRisk);
      setRiskDistribution(analyzeDocumentsForRisk(formatted));
    } catch (e) {
      console.error(e);
      const result = await documentService.getDocuments(clientData.id);
      const formatted = result.documents.map((doc) => ({
        ...doc,
        icon: getFileIcon(doc.name || doc.mimeType),
        kind: getFileKind(doc.name || doc.mimeType),
        size: formatFileSize(doc.size),
        date: formatDate(doc.uploadedDate || doc.createdAt),
        hasRiskFlag: checkDocumentForRiskFlags(doc),
      }));
      setDocuments(formatted);
      setRiskDistribution(analyzeDocumentsForRisk(formatted));
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    handleAnalyzeAllDocuments();
  }, [clientData.id]);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        if (!clientData.id || clientData.id === "N/A") {
          setDocuments([]);
          return;
        }
        const response = await documentService.getDocuments(clientData.id);
        const transformed = response.documents.map((doc) => ({
          ...doc,
          icon: getFileIcon(doc.name || doc.mimeType),
          kind: getFileKind(doc.name || doc.mimeType),
          size: formatFileSize(doc.size),
          date: formatDate(doc.uploadedDate || doc.createdAt),
          hasRiskFlag: checkDocumentForRiskFlags(doc),
        }));
        setDocuments(transformed);
        setRiskDistribution(analyzeDocumentsForRisk(transformed));
      } catch (e) {
        console.error(e);
        setDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };
    fetchDocuments();
  }, [clientData.id]);

  // ── Preview & Download ────────────────────────────────────────────────────

  const canPreviewInBrowser = (doc) => {
    const previewable = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "text/plain",
    ];
    if (doc.mimeType && previewable.includes(doc.mimeType)) return true;
    const ext = (doc.name || "").split(".").pop().toLowerCase();
    return ["pdf", "png", "jpg", "jpeg", "gif", "txt"].includes(ext);
  };

  const handlePreviewDocument = async (doc) => {
    try {
      setPreviewLoading(true);
      setCurrentPreviewDocument(doc);
      setPreviewModalOpen(true);
    } catch (e) {
      console.error(e);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenInNewTab = (doc) => {
    if (doc.fileUrl) window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
    else alert("No URL available for this document.");
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setCurrentPreviewDocument(null);
    setPreviewLoading(false);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      if (doc.id) {
        const blob = await documentService.downloadDocument(doc.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = doc.name || "document";
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else throw new Error("No document ID");
    } catch (e) {
      console.error(e);
      if (doc.fileUrl) {
        const link = document.createElement("a");
        link.href = doc.fileUrl;
        link.download = doc.name || "document";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else alert("Unable to download document.");
    }
  };

  const handleDownloadAll = async () => {
    if (!documents.length) {
      alert("No documents available.");
      return;
    }
    try {
      if (clientData.id && clientData.id !== "N/A") {
        const blob = await documentService.downloadAllDocuments(clientData.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `documents-${clientData.name}-${new Date().toISOString().split("T")[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }
    } catch (e) {
      console.error(e);
      alert("Unable to download documents.");
    }
  };

  // ── Graph data ────────────────────────────────────────────────────────────

  const getGraphData = () => {
    let baseAmount = 46000;
    if (clientData.amount) {
      const n = parseFloat(clientData.amount.replace(/[£$,]/g, ""));
      if (!isNaN(n)) baseAmount = n;
    }
    const riskLevel = clientData.risk.toLowerCase();
    let dueDate;
    try {
      const [day, month, year] = clientData.dueDate.split(" ");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const mi = monthNames.findIndex(
        (m) => m.toLowerCase() === month.toLowerCase(),
      );
      dueDate =
        mi !== -1
          ? new Date(parseInt(year), mi, parseInt(day))
          : new Date(2025, 10, 11);
    } catch {
      dueDate = new Date(2025, 10, 11);
    }

    const now = new Date();
    const profiles = {
      high: {
        monthlyPaymentRate: 0.08,
        aiOptimism: 0.7,
        paymentConsistency: 0.6,
      },
      medium: {
        monthlyPaymentRate: 0.12,
        aiOptimism: 0.85,
        paymentConsistency: 0.75,
      },
      low: {
        monthlyPaymentRate: 0.18,
        aiOptimism: 0.95,
        paymentConsistency: 0.9,
      },
    };
    const profile = profiles[riskLevel] || profiles.medium;

    let timeUntilDue, totalTimePoints, dateFormatter, stepMs;
    switch (timePeriod) {
      case "days":
        timeUntilDue = Math.ceil((dueDate - now) / 86400000);
        totalTimePoints = Math.max(30, timeUntilDue + 15);
        stepMs = 86400000;
        dateFormatter = (d) =>
          `${d.getDate()} ${d.toLocaleDateString("en-US", { month: "short" })}`;
        break;
      case "weeks":
        timeUntilDue = Math.ceil((dueDate - now) / (7 * 86400000));
        totalTimePoints = Math.max(12, timeUntilDue + 6);
        stepMs = 7 * 86400000;
        dateFormatter = (d) =>
          `W${Math.ceil((d.getDate() + 1) / 7)} ${d.toLocaleDateString("en-US", { month: "short" })}`;
        break;
      default:
        timeUntilDue = Math.ceil((dueDate - now) / (30.44 * 86400000));
        totalTimePoints = Math.max(12, timeUntilDue + 6);
        stepMs = 30.44 * 86400000;
        dateFormatter = (d) =>
          `${d.toLocaleDateString("en-US", { month: "short" })} '${d.getFullYear().toString().slice(-2)}`;
    }

    const data = [];
    for (let i = 0; i < totalTimePoints; i++) {
      const currentDate = new Date(now.getTime() + i * stepMs);
      const timeFromDue = i - timeUntilDue;
      let rate = profile.monthlyPaymentRate;
      if (timePeriod === "days") rate /= 30;
      if (timePeriod === "weeks") rate /= 4;

      let opt, real;
      if (timeFromDue <= 0) {
        const progress = Math.min(timeUntilDue, i) / Math.max(1, timeUntilDue);
        opt = Math.max(
          baseAmount * 0.3,
          baseAmount * (1 - progress * rate * profile.aiOptimism),
        );
        real = Math.max(
          baseAmount * 0.3,
          baseAmount * (1 - progress * rate * profile.paymentConsistency),
        );
      } else {
        const sf = Math.max(
          0.1,
          1 -
            timeFromDue *
              (timePeriod === "days"
                ? 0.02
                : timePeriod === "weeks"
                  ? 0.1
                  : 0.15),
        );
        opt = Math.max(
          0,
          baseAmount *
            0.4 *
            Math.pow(1 - rate * profile.aiOptimism * sf, timeFromDue),
        );
        real = Math.max(
          0,
          baseAmount *
            0.6 *
            Math.pow(1 - rate * profile.paymentConsistency * sf, timeFromDue),
        );
      }
      data.push({
        date: dateFormatter(currentDate),
        actual: Math.max(baseAmount * 0.05, Math.round(opt * 100) / 100),
        predicted: Math.max(baseAmount * 0.05, Math.round(real * 100) / 100),
        isDueDate: timeFromDue === 0,
      });
    }
    return data;
  };

  const getTimelineInsights = () => {
    let base = 46000;
    if (clientData.amount) {
      const n = parseFloat(clientData.amount.replace(/[£$,]/g, ""));
      if (!isNaN(n)) base = n;
    }
    const gd = getGraphData();
    const di = gd.findIndex((p) => p.isDueDate);
    const proj =
      di >= 0 && di + 6 < gd.length
        ? gd[di + 6].actual
        : gd[gd.length - 1]?.actual || base;
    const map = {
      high: `High risk suggests extended payment timeline. Expected to owe ~£${Math.round(proj).toLocaleString()} six months post-due date. Consider proactive collection measures.`,
      medium: `Moderate risk indicates gradual repayment. Projected balance of ~£${Math.round(proj).toLocaleString()} six months post-due date. Regular monitoring advised.`,
      low: `Low risk profile shows strong repayment potential. Expected to reduce balance to ~£${Math.round(proj).toLocaleString()} within six months post-due date.`,
    };
    return map[clientData.risk.toLowerCase()] || map.medium;
  };

  const getSuggestedActions = () => {
    switch (clientData.risk.toLowerCase()) {
      case "high":
        return [
          "Immediate escalation to legal team due to high risk profile",
          "Consider freezing additional credit facilities",
          "Schedule urgent client meeting to discuss payment plan",
        ];
      case "medium":
        return [
          "Escalate to Post Pre-LBA due to missed payments",
          "Offer structured re-payment plans",
          "Monitor closely for any deterioration",
        ];
      case "low":
        return [
          "Continue with standard collection procedures",
          "Send payment reminder notices",
          "Maintain regular client communication",
        ];
      default:
        return [
          "Review client risk assessment",
          "Determine appropriate collection strategy",
          "Monitor payment behavior",
        ];
    }
  };

  const graph_data = getGraphData();
  const pie_data = [
    { name: "Risk flags", value: riskDistribution.riskFlags },
    { name: "Safe documents", value: riskDistribution.safeDocuments },
  ];
  const PIE_COLORS = ["#EF4444", "#10B981"];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 8,
          padding: "12px 16px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          fontFamily: "DM Sans,sans-serif",
        }}
      >
        <p
          style={{
            margin: "0 0 8px",
            fontWeight: 700,
            fontSize: 13,
            color: "#0F172A",
          }}
        >
          {label}
        </p>
        {d.isDueDate && (
          <div
            style={{
              marginBottom: 8,
              padding: "4px 8px",
              background: "#FFFBEB",
              border: "1px solid #FCD34D",
              borderRadius: 6,
              fontSize: 12,
              color: "#92400E",
              fontWeight: 600,
            }}
          >
            📅 Due Date
          </div>
        )}
        <p style={{ margin: "2px 0", fontSize: 12, color: "#2563EB" }}>
          Current trend: <strong>£{d.actual.toFixed(2)}</strong>
        </p>
        <p style={{ margin: "2px 0", fontSize: 12, color: "#10B981" }}>
          AI optimistic: <strong>£{d.predicted.toFixed(2)}</strong>
        </p>
        <p style={{ margin: "6px 0 0", fontSize: 11, color: "#94A3B8" }}>
          Based on {clientData.risk.toLowerCase()} risk patterns
        </p>
      </div>
    );
  };

  const breadcrumbs = [
    { label: "AI Indicators", path: "/ai1" },
    { label: clientData.name, active: true },
  ];

  // filtered docs
  const filteredDocs = documents.filter((doc) => {
    const matchSearch = (doc.name || "")
      .toLowerCase()
      .includes(docSearch.toLowerCase());
    const matchFilter =
      docFilter === "all" ||
      (docFilter === "pdf" &&
        (doc.name || "").toLowerCase().endsWith(".pdf")) ||
      (docFilter === "image" &&
        ["png", "jpg", "jpeg", "gif"].some((e) =>
          (doc.name || "").toLowerCase().endsWith(e),
        )) ||
      (docFilter === "word" &&
        ["doc", "docx"].some((e) =>
          (doc.name || "").toLowerCase().endsWith(e),
        ));
    return matchSearch && matchFilter;
  });

  const closeLogoutConfirmation = useCallback(
    () => setLogoutConfirmationOpen(false),
    [],
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');

        html, body { background: rgb(241,245,249) !important; }

        .ai2-root * { box-sizing: border-box; }
        .ai2-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241,245,249) !important;
          min-height: 100vh;
        }

        .ai2-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          position: relative;
          overflow: hidden;
        }

        .ai2-metric-card {
          padding: 24px;
          cursor: default;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .ai2-metric-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 24px rgba(0,0,0,0.09);
        }
        .ai2-accent-bar {
          position: absolute; top: 0; left: 0; right: 0;
          height: 4px; border-radius: 16px 16px 0 0;
        }

        .ai2-icon-box {
          width: 40px; height: 40px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #E2E8F0; margin-top: 8px; margin-bottom: 4px;
        }

        .ai2-metric-label {
          font-size: 12px; font-weight: 600; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px;
        }

        .ai2-metric-value {
          font-family: 'DM Mono', monospace;
          font-size: 22px; font-weight: 500; color: #0F172A; line-height: 1.2;
        }

        .ai2-section-header {
          padding: 18px 24px;
          border-bottom: 1px solid #E2E8F0;
          display: flex; align-items: center; justify-content: space-between;
        }

        .ai2-section-title { font-size: 16px; font-weight: 700; color: #0F172A; }

        .ai2-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
          font-size: 12px; font-weight: 700; color: #2563EB;
        }

        .ai2-tab-row {
          display: inline-flex; background: #F1F5F9;
          border-radius: 8px; padding: 3px; gap: 2px;
        }
        .ai2-tab {
          padding: 6px 16px; border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748B;
          cursor: pointer; border: none; background: transparent;
          transition: background 0.15s, color 0.15s;
        }
        .ai2-tab:hover { background: #ffffff; color: #0F172A; }
        .ai2-tab.active {
          background: #ffffff; color: #2563EB;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }

        .ai2-filter-row {
          padding: 12px 24px;
          border-bottom: 1px solid #F1F5F9;
          display: flex; align-items: center; justify-content: space-between;
          background: #FAFBFD;
        }

        .ai2-search {
          display: flex; align-items: center; gap: 8px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          padding: 8px 14px; width: 240px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .ai2-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .ai2-search input {
          border: none; outline: none; background: transparent;
          font-family: 'DM Sans', sans-serif; font-size: 13px; color: #0F172A; width: 100%;
        }
        .ai2-search input::placeholder { color: #94A3B8; }

        .ai2-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; background: #ffffff;
          border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .ai2-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .ai2-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .ai2-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 9px 18px; border-radius: 10px; border: none;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700;
          background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
          color: #ffffff; cursor: pointer;
          box-shadow: 0 2px 8px rgba(37,99,235,0.3);
          transition: opacity 0.15s, transform 0.15s;
        }
        .ai2-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }

        .ai2-btn-danger {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 9px 18px; border-radius: 10px; border: none;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 700;
          background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
          color: #ffffff; cursor: pointer;
          box-shadow: 0 2px 8px rgba(239,68,68,0.3);
          transition: opacity 0.15s, transform 0.15s;
        }
        .ai2-btn-danger:hover { opacity: 0.9; transform: translateY(-1px); }

        .ai2-table { width: 100%; border-collapse: collapse; }
        .ai2-table thead tr { background: #F8FAFC; }
        .ai2-table th {
          padding: 11px 16px; text-align: left;
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          border-bottom: 1px solid #E2E8F0;
        }
        .ai2-table td {
          padding: 13px 16px; font-size: 13px; color: #1E293B;
          border-bottom: 1px solid #F1F5F9; vertical-align: middle;
        }
        .ai2-table tbody tr { transition: background 0.12s; }
        .ai2-table tbody tr:hover { background: #F8FAFC; }
        .ai2-table tbody tr:last-child td { border-bottom: none; }

        .ai2-risk-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; border-radius: 20px;
          font-size: 12px; font-weight: 700;
        }

        .ai2-insight-box {
          padding: 14px 18px;
          background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 10px;
          font-size: 13px; color: #1E40AF;
        }

        .ai2-actions-card {
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 16px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }

        .ai2-action-item {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 0; border-bottom: 1px solid #F1F5F9;
        }
        .ai2-action-item:last-child { border-bottom: none; padding-bottom: 0; }

        .ai2-action-number {
          width: 24px; height: 24px; border-radius: 50%;
          background: #EFF6FF; color: #2563EB;
          font-size: 12px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
        }

        .ai2-legend-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }

        .ai2-icon-btn {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #E2E8F0; background: #ffffff; cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
        }
        .ai2-icon-btn:hover { border-color: #2563EB; background: #EFF6FF; }
        .ai2-icon-btn.disabled { opacity: 0.4; cursor: not-allowed; }
        .ai2-icon-btn.disabled:hover { border-color: #E2E8F0; background: #ffffff; }
      `}</style>

      <div
        className="ai2-root w-full overflow-visible"
        style={{ background: "rgb(241,245,249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* Breadcrumb */}
          <Breadcrumb breadcrumbs={breadcrumbs} />

          {/* Page header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                Client: {clientData.name}
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
                Detailed AI insights & debt profile.
              </p>
            </div>
            {/* <div style={{ display: "flex", gap: 10 }}>
              <button className="ai2-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16.44 8.9c3.6.31 5.07 2.16 5.07 6.21v.13c0 4.47-1.79 6.26-6.26 6.26H8.74c-4.47 0-6.26-1.79-6.26-6.26v-.13c0-4.02 1.45-5.87 4.99-6.2M12 15V3.62M15.35 5.85L12 2.5 8.65 5.85"/>
                </svg>
                Export
              </button>
              <button className="ai2-btn-danger">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9v5M12 21H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12Z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11.995 17h.009" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Escalate case
              </button>
            </div> */}
          </div>

          {/* Metric cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 18,
            }}
          >
            {/* Debt amount */}
            <div className="ai2-card ai2-metric-card">
              <div
                className="ai2-accent-bar"
                style={{ background: "#7C3AED" }}
              />
              <div className="ai2-icon-box" style={{ background: "#F5F3FF" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M2 8.5h20M6 16.5h2M10.5 16.5h4"
                    stroke="#7C3AED"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.44 3.5h11.11c3.56 0 4.45.88 4.45 4.39v8.22c0 3.51-.89 4.39-4.45 4.39H6.44C2.89 20.5 2 19.62 2 16.11V7.89C2 4.38 2.89 3.5 6.44 3.5Z"
                    stroke="#7C3AED"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="ai2-metric-label">Debt amount</div>
              <div className="ai2-metric-value">{clientData.amount || "—"}</div>
            </div>

            {/* Risk level */}
            <div
              className="ai2-card ai2-metric-card"
              style={{ background: getRiskBg(clientData.risk) }}
            >
              <div
                className="ai2-accent-bar"
                style={{ background: getRiskAccent(clientData.risk) }}
              />
              <div className="ai2-icon-box" style={{ background: "#ffffff" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 9v5M12 21H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12Z"
                    stroke={getRiskColor(clientData.risk)}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.995 17h.009"
                    stroke={getRiskColor(clientData.risk)}
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="ai2-metric-label">Risk level</div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 2,
                }}
              >
                <div className="ai2-metric-value">{clientData.risk}</div>
                <span
                  className="ai2-risk-badge"
                  style={{
                    background: getRiskBg(clientData.risk),
                    color: getRiskColor(clientData.risk),
                  }}
                >
                  ● {clientData.risk}
                </span>
              </div>
            </div>

            {/* AI score */}
            <div className="ai2-card ai2-metric-card">
              <div
                className="ai2-accent-bar"
                style={{ background: "#2563EB" }}
              />
              <div className="ai2-icon-box" style={{ background: "#EFF6FF" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Z"
                    stroke="#2563EB"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8 12h4M12 8v8"
                    stroke="#2563EB"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="ai2-metric-label">AI score</div>
              <div className="ai2-metric-value">{clientData.aiScore}</div>
            </div>

            {/* Due date */}
            <div className="ai2-card ai2-metric-card">
              <div
                className="ai2-accent-bar"
                style={{ background: "#F59E0B" }}
              />
              <div className="ai2-icon-box" style={{ background: "#FFFBEB" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M8 2v3M16 2v3M3.5 9.09h17M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z"
                    stroke="#D97706"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.695 13.7h.009M15.695 16.7h.009M11.995 13.7h.009M11.995 16.7h.009M8.294 13.7h.01M8.294 16.7h.01"
                    stroke="#D97706"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="ai2-metric-label">Due date</div>
              <div className="ai2-metric-value">{clientData.dueDate}</div>
            </div>
          </div>

          {/* Debt Timeline chart */}
          <div className="ai2-card" style={{ padding: 24 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: 16,
                borderBottom: "1px solid #E2E8F0",
                marginBottom: 20,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                >
                  Debt timeline — {clientData.name}
                </div>
                <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                  AI-projected payment scenarios
                </div>
              </div>
              <div className="ai2-tab-row">
                {[
                  { key: "days", label: "Days" },
                  { key: "weeks", label: "Weeks" },
                  { key: "months", label: "Months" },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`ai2-tab${timePeriod === t.key ? " active" : ""}`}
                    onClick={() => setTimePeriod(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={graph_data}
                  margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fontSize: 11,
                      fill: "#94A3B8",
                      fontFamily: "DM Sans",
                    }}
                    stroke="#E2E8F0"
                  />
                  <YAxis
                    tick={{
                      fontSize: 11,
                      fill: "#94A3B8",
                      fontFamily: "DM Sans",
                    }}
                    stroke="#E2E8F0"
                    tickFormatter={(v) =>
                      v >= 1000000
                        ? `£${(v / 1000000).toFixed(1)}M`
                        : v >= 1000
                          ? `£${(v / 1000).toFixed(0)}k`
                          : `£${v}`
                    }
                  />
                  {graph_data.map(
                    (p, i) =>
                      p.isDueDate && (
                        <ReferenceLine
                          key={i}
                          x={p.date}
                          stroke="#F59E0B"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          label={{
                            value: "Due date",
                            position: "insideTop",
                            fill: "#D97706",
                            fontSize: 11,
                            fontWeight: 700,
                            offset: 8,
                          }}
                        />
                      ),
                  )}
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{
                      fontFamily: "DM Sans",
                      fontSize: 12,
                      paddingTop: 8,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#2563EB"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 5 }}
                    name="Current trend"
                  />
                  <Line
                    type="monotone"
                    dataKey="predicted"
                    stroke="#10B981"
                    strokeWidth={2.5}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={{ r: 5 }}
                    name="AI optimistic scenario"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="ai2-insight-box" style={{ marginTop: 16 }}>
              <strong>AI Insight:</strong> {getTimelineInsights()}
            </div>
          </div>

          {/* Documents + Pie chart row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 320px",
              gap: 18,
            }}
          >
            {/* Documents table */}
            <div className="ai2-card">
              <div className="ai2-section-header">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="ai2-section-title">Documents</span>
                  <span className="ai2-count-pill">
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#2563EB",
                        display: "inline-block",
                      }}
                    />
                    {loadingDocuments
                      ? "Loading…"
                      : `${documents.length} ${documents.length === 1 ? "document" : "documents"}`}
                  </span>
                </div>
                <button
                  className="ai2-btn"
                  onClick={handleDownloadAll}
                  disabled={loadingDocuments || documents.length === 0}
                >
                  <Download size={15} />
                  Download all
                </button>
              </div>

              <div className="ai2-filter-row">
                <div className="ai2-tab-row">
                  {[
                    { key: "all", label: "View all" },
                    { key: "pdf", label: "PDF" },
                    { key: "image", label: "Image" },
                    { key: "word", label: "Word" },
                  ].map((t) => (
                    <button
                      key={t.key}
                      className={`ai2-tab${docFilter === t.key ? " active" : ""}`}
                      onClick={() => setDocFilter(t.key)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="ai2-search">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2"
                      stroke="#94A3B8"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search documents…"
                    value={docSearch}
                    onChange={(e) => setDocSearch(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table className="ai2-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Kind</th>
                      <th>Uploaded date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDocuments ? (
                      <tr>
                        <td
                          colSpan="4"
                          style={{
                            textAlign: "center",
                            color: "#94A3B8",
                            padding: "32px 16px",
                          }}
                        >
                          Loading documents…
                        </td>
                      </tr>
                    ) : filteredDocs.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          style={{
                            textAlign: "center",
                            color: "#94A3B8",
                            padding: "32px 16px",
                          }}
                        >
                          No documents found.
                        </td>
                      </tr>
                    ) : (
                      filteredDocs.map((doc, idx) => (
                        <tr
                          key={idx}
                          style={
                            doc.hasRiskFlag
                              ? { borderLeft: "3px solid #EF4444" }
                              : {}
                          }
                        >
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              {doc.icon}
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    color: "#0F172A",
                                    fontSize: 13,
                                  }}
                                >
                                  {doc.name}
                                  {doc.hasRiskFlag && (
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        padding: "2px 7px",
                                        background: "#FEF2F2",
                                        color: "#991B1B",
                                        borderRadius: 20,
                                        fontSize: 11,
                                        fontWeight: 700,
                                      }}
                                    >
                                      Risk flag
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#94A3B8",
                                    marginTop: 1,
                                  }}
                                >
                                  {doc.size}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ color: "#64748B" }}>{doc.kind}</td>
                          <td style={{ color: "#64748B", fontSize: 12 }}>
                            {doc.date}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                className={`ai2-icon-btn${!canPreviewInBrowser(doc) ? " disabled" : ""}`}
                                onClick={() =>
                                  canPreviewInBrowser(doc) &&
                                  handlePreviewDocument(doc)
                                }
                                disabled={!canPreviewInBrowser(doc)}
                                title={
                                  canPreviewInBrowser(doc)
                                    ? "Preview"
                                    : "Preview unavailable"
                                }
                              >
                                <Eye
                                  size={15}
                                  style={{
                                    color: canPreviewInBrowser(doc)
                                      ? "#2563EB"
                                      : "#94A3B8",
                                  }}
                                />
                              </button>
                              <button
                                className="ai2-icon-btn"
                                onClick={() => handleDownloadDocument(doc)}
                                title="Download"
                              >
                                <Download
                                  size={15}
                                  style={{ color: "#059669" }}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 24px",
                  borderTop: "1px solid #E2E8F0",
                  background: "#FAFBFD",
                }}
              >
                <button className="ai2-btn" disabled>
                  ← Previous
                </button>
                <span
                  style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}
                >
                  Page 1 of 1
                </span>
                <button className="ai2-btn" disabled>
                  Next →
                </button>
              </div>
            </div>

            {/* Pie chart */}
            <div className="ai2-card" style={{ padding: 24 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#0F172A",
                  paddingBottom: 16,
                  borderBottom: "1px solid #E2E8F0",
                  marginBottom: 16,
                }}
              >
                Risk distribution
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <ResponsiveContainer width={220} height={220}>
                  <PieChart>
                    <Pie
                      data={pie_data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pie_data.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #E2E8F0",
                        borderRadius: 8,
                        fontFamily: "DM Sans",
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {[
                    {
                      label: "Risk flags",
                      color: "#EF4444",
                      value: pie_data[0].value,
                    },
                    {
                      label: "Safe documents",
                      color: "#10B981",
                      value: pie_data[1].value,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: 13,
                          color: "#1E293B",
                        }}
                      >
                        <span
                          className="ai2-legend-dot"
                          style={{ background: item.color }}
                        />
                        {item.label}
                      </div>
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#0F172A",
                        }}
                      >
                        {item.value}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Suggested actions */}
          <div className="ai2-actions-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #BFDBFE",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2L2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5"
                    stroke="#2563EB"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <div
                  style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}
                >
                  Suggested actions
                </div>
                <div style={{ fontSize: 12, color: "#64748B" }}>
                  AI-recommended next steps for this case
                </div>
              </div>
            </div>
            <div>
              {getSuggestedActions().map((action, i) => (
                <div key={i} className="ai2-action-item">
                  <div className="ai2-action-number">{i + 1}</div>
                  <div
                    style={{ fontSize: 14, color: "#1E293B", lineHeight: 1.5 }}
                  >
                    {action}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* Document Preview Modal */}
      {isPreviewModalOpen && (
        <PortalPopup
          overlayColor="rgba(0,0,0,0.5)"
          placement="Centered"
          onOutsideClick={closePreviewModal}
        >
          <div
            style={{
              width: "90vw",
              height: "90vh",
              background: "#fff",
              borderRadius: 16,
              display: "flex",
              flexDirection: "column",
              border: "1px solid #E2E8F0",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#FAFBFD",
              }}
            >
              <div>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                >
                  {currentPreviewDocument?.name || "Document preview"}
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                  {currentPreviewDocument?.kind} ·{" "}
                  {currentPreviewDocument?.size}
                </div>
              </div>
              <button className="ai2-icon-btn" onClick={closePreviewModal}>
                <X size={16} style={{ color: "#64748B" }} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: "hidden", padding: 16 }}>
              {previewLoading ? (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    color: "#64748B",
                    fontSize: 14,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      border: "2px solid #E2E8F0",
                      borderTopColor: "#2563EB",
                      borderRadius: "50%",
                      animation: "spin 0.7s linear infinite",
                    }}
                  />
                  Loading preview…
                </div>
              ) : currentPreviewDocument?.fileUrl ? (
                currentPreviewDocument.mimeType === "application/pdf" ||
                currentPreviewDocument.name?.toLowerCase().endsWith(".pdf") ? (
                  <iframe
                    src={currentPreviewDocument.fileUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      borderRadius: 8,
                    }}
                    title={currentPreviewDocument.name}
                  />
                ) : currentPreviewDocument.mimeType?.startsWith("image/") ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      background: "#F8FAFC",
                      borderRadius: 8,
                    }}
                  >
                    <img
                      src={currentPreviewDocument.fileUrl}
                      alt={currentPreviewDocument.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      gap: 16,
                    }}
                  >
                    <FileText size={56} style={{ color: "#CBD5E1" }} />
                    <p style={{ color: "#64748B", fontSize: 14 }}>
                      Preview not available for this file type.
                    </p>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        className="ai2-btn-primary"
                        onClick={() =>
                          handleOpenInNewTab(currentPreviewDocument)
                        }
                      >
                        Open in new tab
                      </button>
                      <button
                        className="ai2-btn"
                        onClick={() =>
                          handleDownloadDocument(currentPreviewDocument)
                        }
                      >
                        Download
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 16,
                  }}
                >
                  <FileText size={56} style={{ color: "#CBD5E1" }} />
                  <p style={{ color: "#64748B", fontSize: 14 }}>
                    No preview available.
                  </p>
                  <button
                    className="ai2-btn"
                    onClick={() =>
                      handleDownloadDocument(currentPreviewDocument)
                    }
                  >
                    Download file
                  </button>
                </div>
              )}
            </div>

            <div
              style={{
                padding: "14px 24px",
                borderTop: "1px solid #E2E8F0",
                background: "#FAFBFD",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 12, color: "#94A3B8" }}>
                {currentPreviewDocument?.kind} · {currentPreviewDocument?.size}
              </span>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="ai2-btn"
                  onClick={() => handleOpenInNewTab(currentPreviewDocument)}
                >
                  <Eye size={14} /> Open in new tab
                </button>
                <button
                  className="ai2-btn"
                  onClick={() => handleDownloadDocument(currentPreviewDocument)}
                >
                  <Download size={14} /> Download
                </button>
                <button className="ai2-btn" onClick={closePreviewModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </PortalPopup>
      )}

      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(0,0,0,0.25)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default AIIndicators2;
